import { type ReactElement, useCallback, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router';

import { ACCOUNT_ROUTE, AccountScreen } from '../features/account';
import { CHANNELS_ROUTE, ChannelsTabScreen } from '../features/channels';
import { EMERGENCY_STOP_ROUTE, EmergencyStopScreen } from '../features/emergency';
import { HomeScreen } from '../features/home';
import {
  ChannelSelectionRoute,
  FirstSettingsRoute,
  IntroRoute,
  ONBOARDING_ROUTES,
  TelegramLoginRoute,
  TradingAccountRoute,
} from '../features/onboarding';
import { PositionsScreen } from '../features/positions';
import { CHANNEL_SETTINGS_ROUTE, ChannelSettingsScreen } from '../features/settings';

import { AppLayout } from './AppLayout';
import { TABS } from './tabs';

/** Props of {@link AppRoutes}. */
export interface AppRoutesProps {
  /** True when this launch created the trader (S3 R5): `/` then shows E1 once. */
  readonly firstLaunch?: boolean;
}

/**
 * Route table of the Mini App: one route per tab; any unknown path goes back to Home.
 * Home is E6 (M1), Positions is E8 (M5), Account is E11 (S8); Channels is E12 (S9). E1 (S3)
 * is outside the tab layout, and so are E2 (S4), E3 (S5), E4 (S6) and E5 (S7): no tab bar during the entry journey. E7 (M2) is
 * `/channels/:subscriptionId/settings`, inside the layout. E10 (M4) is `/emergency-stop`, inside the layout:
 * the sheet opens over it and the stop summary replaces it.
 *
 * First launch (S3 R5, Proposal): while E1 is pending, `/` redirects to E1. The decision is made
 * before `Routes` renders, so the tab bar and Home never mount and Home never asks for `GET /me`.
 * "Get started" clears it, so `/` shows Home for the rest of the launch (AC9). Deep links are
 * never redirected (AC10). The flag lives in memory only (R12).
 */
export function AppRoutes({ firstLaunch = false }: AppRoutesProps): ReactElement {
  const { pathname } = useLocation();
  const [introPending, setIntroPending] = useState(firstLaunch);
  const introDone = useCallback(() => {
    setIntroPending(false);
  }, []);
  if (introPending && pathname === '/') {
    return <Navigate to={ONBOARDING_ROUTES.intro} replace />;
  }
  return (
    <Routes>
      <Route path={ONBOARDING_ROUTES.intro} element={<IntroRoute onStart={introDone} />} />
      <Route path={ONBOARDING_ROUTES.telegram} element={<TelegramLoginRoute />} />
      <Route path={ONBOARDING_ROUTES.channels} element={<ChannelSelectionRoute />} />
      <Route path={ONBOARDING_ROUTES.tradingAccount} element={<TradingAccountRoute />} />
      {/* E5 · S7 (provisional on Q21): outside the tab layout, no tab bar before Home. */}
      <Route path={ONBOARDING_ROUTES.firstSettings} element={<FirstSettingsRoute />} />
      <Route element={<AppLayout />}>
        {TABS.map((tab) => (
          <Route key={tab.path} path={tab.path} element={screenFor(tab)} />
        ))}
        {/* E7 · M2: Home and the Channels tab link here with channelSettingsPath (M2 R1). */}
        <Route path={CHANNEL_SETTINGS_ROUTE} element={<ChannelSettingsScreen />} />
        {/* E10 · M4: Home's emergency stop button opens it in one tap (M1 R12, M4 R1). */}
        <Route path={EMERGENCY_STOP_ROUTE} element={<EmergencyStopScreen />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

function screenFor(tab: (typeof TABS)[number]): ReactElement {
  switch (tab.path) {
    case '/':
      return <HomeScreen />;
    case CHANNELS_ROUTE:
      return <ChannelsTabScreen />;
    case '/positions':
      return <PositionsScreen />;
    case ACCOUNT_ROUTE:
      return <AccountScreen />;
  }
}
